﻿import {useInitializeMvvmContext, MvvmDataProvider, MvvmDataProviderRef} from "@2d-front-monorepo/design-library";
import {JSX, RefObject, useMemo} from "react";
import {useScrollToFirstError} from "../helpers/scrollToFirstError";
import {TariffId} from "../nominalTypes/TariffId";
import {CreatePermitModel} from "../types/CreatePermitCommand";
import {InstallationByHostData} from "../types/InstallationByHostData";
import {ComponentWithChildrenParts} from "./ComponentParts";
import FullCreateAttachmentsComponent from "./FullCreateAttachmentsComponent";
import PermitsMvvmErrorCollector from "./PermitsMvvmErrorCollector";

export interface CreatePermitAttachmentsComponentProps extends ComponentWithChildrenParts<CreatePermitModel> {
	tariffId: TariffId;
	initialState: CreatePermitModel;
	installation: InstallationByHostData;
	onSubmit: (formData: CreatePermitModel) => Promise<void>;
	contextRef?: RefObject<MvvmDataProviderRef<CreatePermitModel>>;
}

export default function CreatePermitAttachmentsComponent(props: CreatePermitAttachmentsComponentProps): JSX.Element {
	const {tariffId, initialState, installation, contextRef, onSubmit, header, footer, children} = props;

	const profile = installation.configuration.tariffs.profiles[tariffId];
	if (profile == null) {
		throw new Error(`Tariff profile not found for tariff ${tariffId} while creating permit attachments.`);
	}

	const attachments = useMemo(() => ({
		required: profile.attachments.required,
		optional: profile.attachments.optional
	}), [profile]);

	const {context} = useInitializeMvvmContext({
		initialState: () => {
			return initialState;
		},
		contextRef: contextRef
	}, [initialState]);

	const formRef = useScrollToFirstError(context);

	return (
		<MvvmDataProvider<CreatePermitModel> onSubmit={onSubmit} context={context} ref={formRef}>
			<PermitsMvvmErrorCollector/>
			{header?.(context.state)}
			<FullCreateAttachmentsComponent context={context} attachments={attachments}/>
			{children}
			{footer?.(context.state)}
		</MvvmDataProvider>
	);
}
